import React, { useState } from 'react';
import { Owner, Property, Agency, Deal, DealStage, UserProfile } from '../types';
import { Plus, Search, MapPin, Phone, Mail, Home, ExternalLink, MessageCircle, Building, Edit2, Trash2, Filter, Briefcase, Bell, Upload, FileText, X, CheckCircle, AlertCircle, Loader } from 'lucide-react';
import { AddOwnerModal } from './modals/AddOwnerModal';
import { PropertyModal } from './modals/PropertyModal';
import { AddDealModal } from './modals/AddDealModal';
import { useModal } from './ui/ModalProvider';
import { formatPhoneForDisplay } from '../lib/phoneUtils';
import { storage } from '../services/storage';
import { parseCSV } from '../lib/csvParser';

interface CRMProps {
  owners: Owner[];
  properties: Property[];
  deals: Deal[];
  onAddOwner: (owner: Owner) => void;
  onSaveProperty: (property: Property) => void;
  onDeleteOwner: (id: string) => void;
  onDeleteProperty: (id: string) => void;
  userRole: 'admin' | 'user';
  agencies: Agency[];
  onNavigateToManaged: (id: string) => void;
  onAddDeal: (deal: Deal, owner?: Owner, property?: Property) => Promise<void>;
  userProfile: UserProfile | null;
  selectedAgencyId: string | null;
  onRefreshData: () => void;
  onNavigateToDeal?: (dealId: string, tab?: 'notes' | 'reminders' | 'sequence') => void;
}

export const CRM: React.FC<CRMProps> = ({ owners, properties, deals, onAddOwner, onSaveProperty, onDeleteOwner, onDeleteProperty, userRole, agencies, onNavigateToManaged, onAddDeal, userProfile, selectedAgencyId, onRefreshData, onNavigateToDeal }) => {
  const { confirm, toast } = useModal();
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState<'ALL' | 'CLIENT' | 'PROSPECT'>('ALL');
  
  // Modal States
  const [isOwnerModalOpen, setIsOwnerModalOpen] = useState(false);
  const [editingOwner, setEditingOwner] = useState<Owner | null>(null);

  const [isPropertyModalOpen, setIsPropertyModalOpen] = useState(false);
  const [propertyToEdit, setPropertyToEdit] = useState<Property | null>(null);
  const [selectedOwnerId, setSelectedOwnerId] = useState<string>('');

  const [isDealModalOpen, setIsDealModalOpen] = useState(false);
  const [initialOwnerIdForDeal, setInitialOwnerIdForDeal] = useState<string | undefined>(undefined);
  const [initialPropertyIdForDeal, setInitialPropertyIdForDeal] = useState<string | undefined>(undefined);

  // Import CSV states
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvPreview, setCsvPreview] = useState<string[][]>([]);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState<{ current: number; total: number } | null>(null);
  const [importResults, setImportResults] = useState<{
    success: number;
    errors: number;
    duplicates: number;
    details: Array<{ row: number; success: boolean; error?: string; duplicate?: boolean }>;
  } | null>(null);
  const [skipDuplicates, setSkipDuplicates] = useState(true);

  // Determine if owner is client
  const getOwnerType = (ownerId: string): 'CLIENT' | 'PROSPECT' => {
      // Is Client if has Managed Property OR Signed Deal
      const hasManagedProp = properties.some(p => p.ownerId === ownerId && p.status === 'MANAGED');
      const hasWonDeal = deals.some(d => d.ownerId === ownerId && d.stage === DealStage.SIGNED);
      
      return (hasManagedProp || hasWonDeal) ? 'CLIENT' : 'PROSPECT';
  };

  const filteredOwners = owners.filter((owner) => {
      const matchesSearch = (owner.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
                            (owner.firstName || '').toLowerCase().includes(searchTerm.toLowerCase());
      
      if (!matchesSearch) return false;

      if (filterStatus === 'ALL') return true;
      
      const type = getOwnerType(owner.id!);
      return type === filterStatus;
  });

  // Helper: Clean phone for WhatsApp link
  const formatPhoneForLink = (phone: string) => {
      let clean = (phone || '').replace(/[^\d+]/g, '');
      if (clean.startsWith('0')) clean = '+33' + clean.substring(1);
      if (!clean.startsWith('+')) clean = '+33' + clean; // Fallback
      return clean;
  };

  const getAgencyName = (id?: string | null) => {
      if (!id) return null;
      return agencies.find(a => a.id === id)?.city;
  };

  // --- OWNER ACTIONS ---
  const handleEditOwner = (owner: Owner) => {
      setEditingOwner(owner);
      setIsOwnerModalOpen(true);
  };

  const handleDeleteOwner = async (owner: Owner) => {
      if (await confirm({
          title: "Suppression du contact",
          message: `Êtes-vous sûr de vouloir supprimer ${owner.firstName} ${owner.lastName} ? Cette action supprimera également ses biens et affaires en cours.`,
          confirmText: "Supprimer",
          danger: true
      })) {
          if (owner.id) onDeleteOwner(owner.id);
      }
  };

  // --- PROPERTY ACTIONS ---
  const handleAddProperty = (ownerId: string) => {
      if (!ownerId) return;
      setSelectedOwnerId(ownerId);
      setPropertyToEdit(null); // Mode création
      setIsPropertyModalOpen(true);
  };

  const handleEditProperty = (property: Property) => {
      setSelectedOwnerId(property.ownerId);
      setPropertyToEdit(property); // Mode édition
      setIsPropertyModalOpen(true);
  };

  const handleDeleteProp = async (prop: Property) => {
      if (await confirm({
          title: "Suppression du bien",
          message: `Supprimer le bien situé à ${prop.address} ?`,
          confirmText: "Supprimer",
          danger: true
      })) {
          if (prop.id) onDeleteProperty(prop.id);
      }
  };

  // --- DEAL ACTIONS ---
  const handleCreateDeal = (property: Property) => {
      setInitialOwnerIdForDeal(property.ownerId);
      setInitialPropertyIdForDeal(property.id);
      setIsDealModalOpen(true);
  };

  const handleCloseDealModal = () => {
      setIsDealModalOpen(false);
      setInitialOwnerIdForDeal(undefined);
      setInitialPropertyIdForDeal(undefined);
  };

  const closeOwnerModal = () => {
      setIsOwnerModalOpen(false);
      setEditingOwner(null);
  };

  // --- CSV IMPORT HANDLERS ---
  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.csv')) {
      toast.error('Veuillez sélectionner un fichier CSV');
      return;
    }

    setCsvFile(file);
    setImportResults(null);
    setImportProgress(null);

    // Lire et prévisualiser le fichier
    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;
      // Utiliser le parser CSV pour obtenir les vraies lignes logiques
      const parsedRows = parseCSV(content);
      
      if (parsedRows.length === 0) {
        setCsvPreview([]);
        return;
      }

      // Récupérer les headers depuis la première ligne parsée
      const headers = Object.keys(parsedRows[0]);
      
      // Créer l'aperçu avec headers + 5 premières lignes de données
      const preview: string[][] = [headers];
      
      // Ajouter les 5 premières lignes de données
      for (let i = 0; i < Math.min(5, parsedRows.length); i++) {
        const row = parsedRows[i];
        const rowValues = headers.map(header => row[header] || '');
        preview.push(rowValues);
      }
      
      setCsvPreview(preview);
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!csvFile) return;

    // Vérifier qu'une agence est sélectionnée
    if (!selectedAgencyId) {
      toast.error('Veuillez sélectionner une agence avant d\'importer des leads');
      return;
    }

    setIsImporting(true);
    setImportResults(null);

    try {
      const content = await csvFile.text();

      const results = await storage.importLeadsFromCSV(content, selectedAgencyId, {
        skipDuplicates
      });

      setImportResults(results);

      // Afficher un message de succès détaillé
      if (results.success > 0) {
        toast.success(`Import réussi : ${results.success} lead${results.success > 1 ? 's' : ''} importé${results.success > 1 ? 's' : ''}${results.errors > 0 ? `, ${results.errors} erreur${results.errors > 1 ? 's' : ''}` : ''}${results.duplicates > 0 ? `, ${results.duplicates} doublon${results.duplicates > 1 ? 's' : ''}` : ''}`);
        
        // Recharger les données
        onRefreshData();
      } else {
        toast.error(`Aucun lead importé. ${results.errors > 0 ? `${results.errors} erreur${results.errors > 1 ? 's' : ''}` : ''}${results.duplicates > 0 ? `${results.duplicates} doublon${results.duplicates > 1 ? 's' : ''}` : ''}`);
      }
    } catch (error: any) {
      console.error('Erreur import:', error);
      toast.error(`Erreur lors de l'import : ${error.message || 'Erreur inconnue'}`);
    } finally {
      setIsImporting(false);
      setImportProgress(null);
    }
  };

  const closeImportModal = () => {
    setIsImportModalOpen(false);
    setCsvFile(null);
    setCsvPreview([]);
    setImportResults(null);
    setImportProgress(null);
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h2 className="text-2xl font-bold text-slate-900">Contacts & Biens</h2>
          <p className="text-slate-500">Gérez vos contacts et leurs biens immobiliers</p>
        </div>
        <div className="flex gap-2">
          <button 
              onClick={() => setIsImportModalOpen(true)}
              className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg flex items-center shadow-sm transition-colors"
          >
            <Upload className="w-4 h-4 mr-2" />
            Importer CSV
          </button>
          <button 
              onClick={() => setIsOwnerModalOpen(true)}
              className="bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg flex items-center shadow-sm transition-colors"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nouveau Contact
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="p-4 border-b border-slate-200 bg-slate-50 flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Rechercher un propriétaire..."
              className="w-full pl-10 pr-4 py-2 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-transparent bg-white text-slate-900"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Filter Buttons */}
          <div className="flex bg-white p-1 rounded-lg border border-slate-200 shadow-sm">
              <button 
                onClick={() => setFilterStatus('ALL')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'ALL' ? 'bg-slate-900 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                  Tous
              </button>
              <button 
                onClick={() => setFilterStatus('CLIENT')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'CLIENT' ? 'bg-green-600 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                  Clients
              </button>
              <button 
                onClick={() => setFilterStatus('PROSPECT')}
                className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${filterStatus === 'PROSPECT' ? 'bg-blue-600 text-white shadow' : 'text-slate-500 hover:bg-slate-50'}`}
              >
                  Prospects
              </button>
          </div>
        </div>

        <div className="divide-y divide-slate-200">
          {filteredOwners.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                  Aucun contact trouvé.
              </div>
          ) : (
          filteredOwners.map((owner) => {
            const ownerProperties = properties.filter((p) => p.ownerId === owner.id);
            const phoneLink = formatPhoneForLink(owner.phone);
            const agencyName = getAgencyName(owner.agencyId);
            const firstName = owner.firstName || '';
            const lastName = owner.lastName || '';
            const ownerType = getOwnerType(owner.id!);

            return (
              <div key={owner.id} className="p-6 hover:bg-slate-50 transition-colors group relative">
                
                <div className="flex flex-col md:flex-row md:items-start md:justify-between">
                  {/* Left Column: Owner Info */}
                  <div className="flex items-start space-x-4 mb-6 md:mb-0 md:w-1/2">
                    <div className="flex flex-col items-center mr-2">
                        <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center text-slate-600 font-bold text-lg uppercase shrink-0">
                        {firstName.charAt(0)}{lastName.charAt(0)}
                        </div>
                        {/* Owner Actions - Moved under Photo */}
                        <div className="flex items-center space-x-1 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                            <button 
                                onClick={() => handleEditOwner(owner)}
                                className="p-1.5 text-slate-400 hover:text-amber-500 hover:bg-slate-100 rounded-full transition-colors"
                                title="Modifier le propriétaire"
                            >
                                <Edit2 className="w-3.5 h-3.5" />
                            </button>
                            {userRole === 'admin' && (
                                <button 
                                    onClick={() => handleDeleteOwner(owner)}
                                    className="p-1.5 text-slate-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                                    title="Supprimer le propriétaire"
                                >
                                    <Trash2 className="w-3.5 h-3.5" />
                                </button>
                            )}
                        </div>
                    </div>
                    <div>
                      <div className="flex items-center flex-wrap gap-2">
                          <h3 className="text-lg font-semibold text-slate-900">
                            {firstName} {lastName}
                          </h3>
                          
                          {/* Client Badge */}
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded border uppercase tracking-wider ${ownerType === 'CLIENT' ? 'bg-green-50 text-green-700 border-green-200' : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                              {ownerType}
                          </span>

                          {userRole === 'admin' && (
                              <span className={`flex items-center text-[10px] font-bold uppercase px-2 py-0.5 rounded-full border ${agencyName ? 'bg-slate-800 text-white border-slate-800' : 'bg-slate-100 text-slate-400 border-slate-200'}`} title="Agence de rattachement">
                                  <Building className="w-3 h-3 mr-1" />
                                  {agencyName || 'Sans Agence'}
                              </span>
                          )}
                          {owner.source && (
                              <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full border border-amber-200">
                                  {owner.source}
                              </span>
                          )}
                          {owner.newsletterOptin && (
                              <span className="text-[10px] bg-green-50 text-green-600 px-2 py-0.5 rounded-full border border-green-200 flex items-center" title="Inscrit à la newsletter Placid">
                                  <Bell className="w-2.5 h-2.5 mr-1" />
                                  Newsletter
                              </span>
                          )}
                      </div>
                      
                      <div className="mt-2 flex flex-col space-y-1.5">
                        <div className="flex items-center space-x-2">
                            <a 
                                href={`mailto:${owner.email}`} 
                                className="flex items-center text-sm text-slate-500 hover:text-amber-600 bg-white border border-slate-200 px-2 py-1 rounded transition-colors"
                            >
                                <Mail className="w-3.5 h-3.5 mr-2" />
                                {owner.email || 'Pas d\'email'}
                            </a>
                        </div>
                        {owner.phone && (
                            <div className="flex items-center space-x-2">
                                <a 
                                    href={`tel:${phoneLink}`}
                                    className="flex items-center text-sm text-slate-500 hover:text-blue-600 bg-white border border-slate-200 px-2 py-1 rounded transition-colors"
                                >
                                    <Phone className="w-3.5 h-3.5 mr-2" />
                                    {formatPhoneForDisplay(owner.phone)}
                                </a>
                                <a 
                                    href={`https://wa.me/${phoneLink.replace('+','')}`}
                                    target="_blank"
                                    rel="noreferrer"
                                    className="flex items-center text-sm text-green-600 hover:text-green-700 bg-green-50 border border-green-200 px-2 py-1 rounded transition-colors"
                                    title="Ouvrir WhatsApp"
                                >
                                    <MessageCircle className="w-3.5 h-3.5 mr-1" />
                                    WhatsApp
                                </a>
                            </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Properties */}
                  <div className="md:w-1/2 md:pl-6 border-l border-slate-100/0 md:border-slate-100">
                    <div className="flex justify-between items-center mb-3">
                        <h4 className="text-xs font-semibold text-slate-400 uppercase tracking-wider">
                            Biens Immobiliers ({ownerProperties.length})
                        </h4>
                        <button 
                            onClick={() => handleAddProperty(owner.id!)}
                            className="text-xs bg-slate-100 hover:bg-amber-100 text-slate-600 hover:text-amber-700 px-2 py-1 rounded-full flex items-center transition-colors font-medium"
                        >
                            <Plus className="w-3 h-3 mr-1" /> Ajouter
                        </button>
                    </div>
                    
                    {ownerProperties.length > 0 ? (
                      <div className="grid gap-2">
                        {ownerProperties.map((prop) => {
                            const propertyDeal = deals.find(d => d.propertyId === prop.id);
                            const hasDeal = !!propertyDeal;
                            return (
                          <div key={prop.id} className="flex items-start justify-between bg-white border border-slate-200 p-3 rounded-lg group/prop hover:border-amber-200 transition-colors">
                            <div className="flex items-start flex-1">
                                <Home className="w-5 h-5 text-amber-500 mr-3 mt-0.5 shrink-0" />
                                <div className="flex-1">
                                    <p className="text-sm font-medium text-slate-900">
                                        {prop.address}{prop.city ? `, ${prop.city}` : ''}
                                    </p>
                                    <div className="flex flex-wrap text-xs text-slate-500 mt-0.5 gap-x-2 items-center">
                                        <span>{prop.type}</span>
                                        <span>•</span>
                                        <span>{prop.surface}m²</span>
                                        {prop.status === 'MANAGED' && (
                                            <span 
                                                onClick={(e) => { e.stopPropagation(); onNavigateToManaged(prop.id!); }}
                                                className="text-[9px] bg-green-100 text-green-700 px-1 rounded border border-green-200 ml-1 cursor-pointer hover:bg-green-200 hover:border-green-300 transition-colors"
                                                title="Voir le bien en gestion"
                                            >
                                                EN GESTION
                                            </span>
                                        )}
                                        {prop.status === 'ARCHIVED' && (
                                            <span 
                                                onClick={(e) => { e.stopPropagation(); onNavigateToManaged(prop.id!); }}
                                                className="text-[9px] bg-amber-100 text-amber-700 px-1 rounded border border-amber-200 ml-1 cursor-pointer hover:bg-amber-200 hover:border-amber-300 transition-colors"
                                                title="Voir le bien archivé"
                                            >
                                                ARCHIVÉ
                                            </span>
                                        )}
                                        {hasDeal && onNavigateToDeal && propertyDeal?.id && (
                                            <button
                                                onClick={(e) => { 
                                                    e.stopPropagation(); 
                                                    onNavigateToDeal(propertyDeal.id!, 'notes');
                                                }}
                                                className="text-[10px] bg-blue-100 text-blue-700 hover:bg-blue-200 px-2 py-0.5 rounded-full border border-blue-200 hover:border-blue-300 transition-colors flex items-center font-bold"
                                                title="Voir l'affaire associée"
                                            >
                                                <Briefcase className="w-3 h-3 mr-1" />
                                                Voir l'affaire
                                            </button>
                                        )}
                                        {!hasDeal && (
                                            <button
                                                onClick={(e) => { e.stopPropagation(); handleCreateDeal(prop); }}
                                                className="text-[10px] bg-amber-100 text-amber-700 hover:bg-amber-200 px-2 py-0.5 rounded-full border border-amber-200 hover:border-amber-300 transition-colors flex items-center font-bold"
                                                title="Créer une affaire pour ce bien"
                                            >
                                                <Briefcase className="w-3 h-3 mr-1" />
                                                Créer une affaire
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            
                            <div className="flex items-center space-x-1 opacity-0 group-hover/prop:opacity-100 transition-opacity">
                                <button 
                                    onClick={() => handleEditProperty(prop)}
                                    className="p-1.5 text-slate-300 hover:text-amber-500 hover:bg-slate-100 rounded transition-colors"
                                    title="Modifier ce bien"
                                >
                                    <Edit2 className="w-3.5 h-3.5" />
                                </button>
                                {userRole === 'admin' && (
                                    <button 
                                        onClick={() => handleDeleteProp(prop)}
                                        className="p-1.5 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                                        title="Supprimer ce bien"
                                    >
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                )}
                            </div>
                          </div>
                        );})}
                      </div>
                    ) : (
                      <div className="text-sm text-slate-400 italic bg-slate-50 p-3 rounded border border-dashed border-slate-200 text-center">
                          Aucun bien enregistré
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          }))}
        </div>
      </div>
      
      {/* OWNER MODAL */}
      <AddOwnerModal 
        isOpen={isOwnerModalOpen} 
        onClose={closeOwnerModal} 
        onSave={onAddOwner} 
        agencies={agencies}
        ownerToEdit={editingOwner}
      />

      {/* PROPERTY MODAL */}
      <PropertyModal
        isOpen={isPropertyModalOpen}
        onClose={() => setIsPropertyModalOpen(false)}
        onSave={onSaveProperty}
        ownerId={selectedOwnerId}
        propertyToEdit={propertyToEdit}
      />

      {/* DEAL MODAL */}
      <AddDealModal
        isOpen={isDealModalOpen}
        onClose={handleCloseDealModal}
        onSave={async (deal, owner, property) => {
          await onAddDeal(deal, owner, property);
          handleCloseDealModal();
          toast.success("Affaire créée avec succès.");
        }}
        owners={owners}
        properties={properties}
        agencies={agencies}
        userProfile={userProfile}
        initialOwnerId={initialOwnerIdForDeal}
        initialPropertyId={initialPropertyIdForDeal}
      />

      {/* CSV IMPORT MODAL */}
      {isImportModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Upload className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">Importer des leads depuis CSV</h3>
                    <p className="text-sm text-slate-500">Importez vos leads depuis l'ancien CRM</p>
                  </div>
                </div>
                <button
                  onClick={closeImportModal}
                  className="p-2 hover:bg-slate-100 rounded-lg transition-colors"
                  disabled={isImporting}
                >
                  <X className="w-5 h-5 text-slate-500" />
                </button>
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
              {!csvFile ? (
                <div className="border-2 border-dashed border-slate-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                  <FileText className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept=".csv"
                      onChange={handleFileSelect}
                      className="hidden"
                    />
                    <span className="text-blue-600 hover:text-blue-700 font-medium">
                      Cliquez pour sélectionner un fichier CSV
                    </span>
                    <p className="text-sm text-slate-500 mt-2">ou glissez-déposez le fichier ici</p>
                  </label>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* File info */}
                  <div className="bg-slate-50 rounded-lg p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <FileText className="w-5 h-5 text-slate-600" />
                      <div>
                        <p className="font-medium text-slate-900">{csvFile.name}</p>
                        <p className="text-sm text-slate-500">{(csvFile.size / 1024).toFixed(2)} KB</p>
                      </div>
                    </div>
                    <button
                      onClick={() => {
                        setCsvFile(null);
                        setCsvPreview([]);
                        setImportResults(null);
                      }}
                      className="text-slate-500 hover:text-slate-700"
                      disabled={isImporting}
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  {/* Preview */}
                  {csvPreview.length > 0 && !importResults && (
                    <div>
                      <h4 className="font-medium text-slate-900 mb-2">Aperçu (5 premières lignes)</h4>
                      <div className="border border-slate-200 rounded-lg overflow-hidden">
                        <div className="overflow-x-auto max-h-64">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50">
                              <tr>
                                {csvPreview[0]?.map((header, idx) => (
                                  <th key={idx} className="px-3 py-2 text-left text-xs font-medium text-slate-700 border-b">
                                    {header.substring(0, 20)}{header.length > 20 ? '...' : ''}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {csvPreview.slice(1, 6).map((row, rowIdx) => (
                                <tr key={rowIdx} className="border-b border-slate-100">
                                  {row.map((cell, cellIdx) => (
                                    <td key={cellIdx} className="px-3 py-2 text-slate-600">
                                      {cell.substring(0, 30)}{cell.length > 30 ? '...' : ''}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Options */}
                  {!importResults && (
                    <div className="space-y-3">
                      {!selectedAgencyId && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <span className="text-sm font-medium text-red-900">
                              Veuillez sélectionner une agence avant d'importer
                            </span>
                          </div>
                        </div>
                      )}
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={skipDuplicates}
                            onChange={(e) => setSkipDuplicates(e.target.checked)}
                            className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm text-slate-700">
                            Ignorer les doublons (owners existants avec même email/téléphone)
                          </span>
                        </label>
                      </div>
                    </div>
                  )}

                  {/* Import results */}
                  {importResults && (
                    <div className="space-y-4">
                      <div className="grid grid-cols-3 gap-4">
                        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <CheckCircle className="w-5 h-5 text-green-600" />
                            <span className="font-medium text-green-900">Succès</span>
                          </div>
                          <p className="text-2xl font-bold text-green-700">{importResults.success}</p>
                        </div>
                        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="w-5 h-5 text-red-600" />
                            <span className="font-medium text-red-900">Erreurs</span>
                          </div>
                          <p className="text-2xl font-bold text-red-700">{importResults.errors}</p>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                          <div className="flex items-center gap-2 mb-1">
                            <AlertCircle className="w-5 h-5 text-amber-600" />
                            <span className="font-medium text-amber-900">Doublons</span>
                          </div>
                          <p className="text-2xl font-bold text-amber-700">{importResults.duplicates}</p>
                        </div>
                      </div>

                      {importResults.details.length > 0 && (
                        <div>
                          <h4 className="font-medium text-slate-900 mb-2">Détails des erreurs</h4>
                          <div className="border border-slate-200 rounded-lg overflow-hidden max-h-64 overflow-y-auto">
                            <table className="w-full text-sm">
                              <thead className="bg-slate-50 sticky top-0">
                                <tr>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700">Ligne</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700">Statut</th>
                                  <th className="px-3 py-2 text-left text-xs font-medium text-slate-700">Message</th>
                                </tr>
                              </thead>
                              <tbody>
                                {importResults.details
                                  .filter(d => !d.success)
                                  .slice(0, 20)
                                  .map((detail, idx) => (
                                    <tr key={idx} className="border-b border-slate-100">
                                      <td className="px-3 py-2 text-slate-600">{detail.row}</td>
                                      <td className="px-3 py-2">
                                        {detail.duplicate ? (
                                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded">Doublon</span>
                                        ) : (
                                          <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded">Erreur</span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 text-slate-600 text-xs">{detail.error || '-'}</td>
                                    </tr>
                                  ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Progress */}
                  {isImporting && importProgress && (
                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                      <div className="flex items-center gap-3 mb-2">
                        <Loader className="w-5 h-5 text-blue-600 animate-spin" />
                        <span className="font-medium text-blue-900">
                          Import en cours... {importProgress.current} / {importProgress.total}
                        </span>
                      </div>
                      <div className="w-full bg-blue-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${(importProgress.current / importProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="p-6 border-t border-slate-200 bg-slate-50 flex justify-end gap-3">
              <button
                onClick={closeImportModal}
                className="px-4 py-2 text-slate-700 hover:bg-slate-200 rounded-lg font-medium transition-colors"
                disabled={isImporting}
              >
                {importResults ? 'Fermer' : 'Annuler'}
              </button>
              {csvFile && !importResults && (
                <button
                  onClick={handleImport}
                  disabled={isImporting || !selectedAgencyId}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  {isImporting ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      Import en cours...
                    </>
                  ) : (
                    <>
                      <Upload className="w-4 h-4" />
                      Importer
                    </>
                  )}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};